import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Chip, Grid2 as Grid, TextField, Tooltip, Typography } from "@mui/material";
import { useRecordContext } from "react-admin";

import { ShowAccordionSection } from "@/components/common/ShowAccordionSection";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { Policy, ResourceName } from "@/types";
import { useMonthlyEnrollments } from "../hooks/useMonthlyEnrollments";
import { deriveCoverageMonths } from "../utils/deriveCoverageMonths";
import { MIN_ENROLLED_LIVES, parseEnrolledLives } from "@/utils/validations/enrollmentValidation";

/**
 * Monthly enrollment section (US 5045 / ADR-047). Shows enrolled lives per
 * calendar coverage month in a compact, height-capped grid of editable cells.
 * Coverage months are derived from the policy effective dates; editing a month
 * upserts that month (create-or-update on the coverage_month key).
 */
export const MonthlyEnrollmentSection = () => {
  const policy = useRecordContext<Policy>();
  const { canEdit } = useResourcePermissions(ResourceName.POLICIES);
  const policyId = policy?.id ? String(policy.id) : undefined;

  const { enrollments, isLoading, isSaving, isDeleting, upsertEnrollment, deleteEnrollment } =
    useMonthlyEnrollments(policyId);

  const months = useMemo(
    () => deriveCoverageMonths(policy?.effective_start_date, policy?.effective_end_date),
    [policy?.effective_start_date, policy?.effective_end_date]
  );

  const livesByMonth = useMemo(() => {
    const map = new Map<string, number>();
    enrollments.forEach((e) => map.set(e.coverage_month, e.enrolled_lives));
    return map;
  }, [enrollments]);

  const recordedCount = livesByMonth.size;
  const totalLives = useMemo(() => Array.from(livesByMonth.values()).reduce((sum, n) => sum + n, 0), [livesByMonth]);

  if (!policy) return null;

  return (
    <ShowAccordionSection label="Monthly Enrollment" defaultExpanded>
      {months.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No coverage period defined for this policy.
        </Typography>
      ) : (
        <>
          {canEdit && recordedCount === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Click a month to record enrolled lives.
            </Typography>
          )}

          <Box sx={{ maxHeight: 320, overflowY: "auto", pr: 0.5 }}>
            <Grid container spacing={1} alignItems="flex-start">
              {months.map((m) => (
                <Grid key={m.coverageMonth} size={{ xs: 6, sm: 6, md: 4 }}>
                  <MonthCell
                    label={m.label}
                    isPartial={m.isPartial}
                    value={livesByMonth.get(m.coverageMonth)}
                    readOnly={!canEdit || isLoading || isSaving || isDeleting}
                    onSave={(lives) =>
                      lives === null ? deleteEnrollment(m.coverageMonth) : upsertEnrollment(m.coverageMonth, lives)
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 1.5,
              pt: 1,
              borderTop: 1,
              borderColor: "divider",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {recordedCount} / {months.length} months recorded
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total: {recordedCount > 0 ? totalLives.toLocaleString() : "—"} lives
            </Typography>
          </Box>
        </>
      )}
    </ShowAccordionSection>
  );
};

interface MonthCellProps {
  label: string;
  isPartial: boolean;
  value: number | undefined;
  readOnly: boolean;
  /** lives === null clears (soft-deletes) the month. */
  onSave: (lives: number | null) => void;
  /** Run dialogs: flags a month the run needs but that has no enrolled lives. */
  isMissing?: boolean;
  /** Run dialogs: marks a value filled by "Copy prior month" and not yet saved. */
  isCopied?: boolean;
  /** Called when the cell enters or leaves an invalid-entry state, so a dialog can block its action. */
  onInvalidChange?: (invalid: boolean) => void;
}

// Exported for unit testing of the input-validation guards.
export const MonthCell = ({
  label,
  isPartial,
  value,
  readOnly,
  onSave,
  isMissing,
  isCopied,
  onInvalidChange,
}: MonthCellProps) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const cellRef = useRef<HTMLDivElement>(null);

  // Report the invalid state while it lasts, including clearing it if the cell
  // unmounts mid-error (e.g. the run month moves and the window shrinks).
  const onInvalidChangeRef = useRef(onInvalidChange);
  useEffect(() => {
    onInvalidChangeRef.current = onInvalidChange;
  });
  const isInvalid = !!error;
  useEffect(() => {
    if (!isInvalid) return;
    onInvalidChangeRef.current?.(true);
    return () => onInvalidChangeRef.current?.(false);
  }, [isInvalid]);

  const hasValue = value !== undefined;

  const startEdit = () => {
    if (readOnly) return;
    setDraft(hasValue ? String(value) : "");
    setError(null);
    setEditing(true);
  };

  const stopEdit = (refocus = false) => {
    setEditing(false);
    setError(null);
    // Keyboard users land back on the cell they were editing.
    if (refocus) requestAnimationFrame(() => cellRef.current?.focus());
  };

  /** An invalid entry keeps the cell in edit mode with an inline error. */
  const commit = (refocus = false) => {
    const input = parseEnrolledLives(draft);
    if (input.kind === "invalid") {
      setError(input.error);
      return;
    }
    stopEdit(refocus);
    if (input.kind === "clear") {
      if (hasValue) onSave(null);
    } else if (input.lives !== value) {
      onSave(input.lives);
    }
  };

  const isEditable = !readOnly && !editing;

  return (
    <Box
      ref={cellRef}
      role={isEditable ? "button" : undefined}
      tabIndex={isEditable ? 0 : undefined}
      aria-label={isEditable ? `Edit ${label} enrolled lives` : undefined}
      sx={{
        borderRadius: 1,
        px: 1,
        py: 0.5,
        minHeight: 52,
        bgcolor: "action.hover",
        border: 1,
        borderColor: isMissing ? "warning.main" : "transparent",
        borderBottomColor: isMissing ? "warning.main" : "divider",
        transition: "background-color 120ms, border-color 120ms",
        ...(!readOnly && {
          cursor: "text",
          "&:hover": { bgcolor: "action.selected", borderBottomColor: "primary.main" },
          "&:focus-visible": { outline: 2, outlineColor: "primary.main", outlineOffset: 1 },
        }),
      }}
      onClick={editing ? undefined : startEdit}
      onKeyDown={(e) => {
        if (isEditable && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          startEdit();
        }
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary" noWrap>
          {label}
        </Typography>
        {isPartial && (
          <Tooltip title="Partial coverage month (mid-month start or end)">
            <Chip label="partial" size="small" sx={{ height: 15, "& .MuiChip-label": { px: 0.5, fontSize: 9 } }} />
          </Tooltip>
        )}
        {isCopied && (
          <Tooltip title="Copied from the prior month; saved when the run succeeds">
            <Chip
              label="copied"
              size="small"
              color="info"
              sx={{ height: 15, "& .MuiChip-label": { px: 0.5, fontSize: 9 } }}
            />
          </Tooltip>
        )}
      </Box>
      {editing ? (
        <TextField
          type="number"
          size="small"
          variant="standard"
          autoFocus
          fullWidth
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setError(null);
          }}
          onBlur={() => commit()}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit(true);
            if (e.key === "Escape") {
              // Cancels this cell only; without this a surrounding dialog closes too.
              e.stopPropagation();
              stopEdit(true);
            }
          }}
          error={!!error}
          helperText={error ?? undefined}
          slotProps={{ htmlInput: { min: MIN_ENROLLED_LIVES, "aria-label": `${label} enrolled lives` } }}
        />
      ) : (
        <Typography variant="body1" sx={{ color: hasValue ? "text.primary" : "text.disabled", lineHeight: 1.6 }}>
          {hasValue ? value : "—"}
        </Typography>
      )}
    </Box>
  );
};
